import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CheckIcon from '@mui/icons-material/Check';
import {
  listCompanyStatuses,
  upsertCompanyStatus,
  REVIEW_STATUS_LABELS_HE,
  REVIEW_STATUS_COLORS,
  type ReviewStatus,
} from '../../api/companyStatus';
import { useCompanyStore } from '../../stores/companyStore';

const STATUSES: ReviewStatus[] = ['not_started', 'in_progress', 'in_review', 'closed'];

/** Small chip showing the active company's review status for the
 * current calendar year. Clicking opens a menu to advance the
 * workflow (לא התחיל → בעבודה → בסקירה → סגור). Hidden if no company
 * is selected.
 */
export default function CompanyStatusChip() {
  const selectedId = useCompanyStore((s) => s.selectedCompanyId);
  const qc = useQueryClient();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  // Default review year = current calendar year. We could let the
  // user pick (multi-year picker) — but for the simple workflow chip
  // this is the year they're likely auditing right now.
  const year = new Date().getFullYear();

  const statusesQ = useQuery({
    queryKey: ['company-status', 'list', selectedId],
    queryFn: listCompanyStatuses,
    enabled: !!selectedId,
    staleTime: 30_000,
  });

  const yearEntry = statusesQ.data?.find((s) => s.year === year) ?? null;
  const currentStatus: ReviewStatus = (yearEntry?.status ?? 'not_started') as ReviewStatus;

  const setMut = useMutation({
    mutationFn: (status: ReviewStatus) =>
      upsertCompanyStatus({ year, status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['company-status'] });
    },
  });

  if (!selectedId) return null;

  const color = REVIEW_STATUS_COLORS[currentStatus];

  return (
    <>
      <Tooltip
        title={
          yearEntry?.updated_by_name
            ? `עודכן ע"י ${yearEntry.updated_by_name}${yearEntry.updated_at ? ` · ${new Date(yearEntry.updated_at).toLocaleDateString('he-IL')}` : ''}`
            : `הגדר סטטוס לשנת ${year}`
        }
        arrow
      >
        <Box
          onClick={(e) => setAnchor(e.currentTarget)}
          sx={{
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            fontSize: 11,
            fontWeight: 700,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            bgcolor: color.bg,
            color: color.fg,
            border: '1px solid',
            borderColor: 'transparent',
            transition: 'border-color .15s',
            '&:hover': { borderColor: color.fg },
            userSelect: 'none',
          }}
        >
          <Box component="span">{REVIEW_STATUS_LABELS_HE[currentStatus]}</Box>
          <Box component="span" sx={{ opacity: 0.6 }}>· {year}</Box>
        </Box>
      </Tooltip>
      <Menu
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { minWidth: 220 } }}
      >
        <Box sx={{ px: 2, py: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            סטטוס סקירה {year}
          </Typography>
        </Box>
        {STATUSES.map((s) => {
          const isCurrent = s === currentStatus;
          const c = REVIEW_STATUS_COLORS[s];
          return (
            <MenuItem
              key={s}
              selected={isCurrent}
              disabled={setMut.isPending}
              onClick={() => {
                if (!isCurrent) setMut.mutate(s);
                setAnchor(null);
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ width: '100%' }}>
                <Box
                  sx={{
                    width: 12, height: 12, borderRadius: '50%',
                    bgcolor: c.fg,
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ flexGrow: 1 }}>{REVIEW_STATUS_LABELS_HE[s]}</Box>
                {isCurrent && <CheckIcon fontSize="small" />}
              </Stack>
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
}
